import { ExtendedFile } from '@consigli/hooks';
import { EmployerRole, Language, SiteDueDiligenceFacilityType } from '@consigli/types';
import {
  createContext,
  useContext,
  useState,
  FC,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';

// Form data that needs to persist across wizard steps
export interface WizardFormData {
  iterationName: string;
  selectedSiteLat: number | null;
  selectedSiteLon: number | null;
  language: Language;
  employerRole: EmployerRole | null;
}

// Default form values
export const defaultFormValues: WizardFormData = {
  iterationName: '',
  selectedSiteLat: null,
  selectedSiteLon: null,
  language: Language.EN,
  employerRole: null,
};

export const FACILITY_TYPES = [
  {
    id: SiteDueDiligenceFacilityType.DATA_CENTER,
    labelKey: 'package-creation.facility-data-center',
  },
  {
    id: SiteDueDiligenceFacilityType.WASTE_WATER,
    labelKey: 'package-creation.facility-waste-water',
  },
  { id: SiteDueDiligenceFacilityType.LOGISTIC, labelKey: 'package-creation.facility-logistic' },
  { id: SiteDueDiligenceFacilityType.BRIDGE, labelKey: 'package-creation.facility-bridge' },
] as const;

export type TemplateMode = 'default' | 'custom';

export const CUSTOMERS = [
  { id: 'customer-a', labelKey: 'package-creation.customer-a' },
  { id: 'customer-b', labelKey: 'package-creation.customer-b' },
  { id: 'customer-c', labelKey: 'package-creation.customer-c' },
] as const;

export type FacilityType = SiteDueDiligenceFacilityType | null;

export interface GisUrl {
  url: string;
  description: string;
}

interface WizardContextValue {
  // Facility type
  facilityType: FacilityType;
  setFacilityType: Dispatch<SetStateAction<FacilityType>>;

  // Template
  templateMode: TemplateMode | null;
  setTemplateMode: Dispatch<SetStateAction<TemplateMode | null>>;
  customTemplateFile: ExtendedFile | null;
  setCustomTemplateFile: Dispatch<SetStateAction<ExtendedFile | null>>;

  // Customer
  selectedCustomer: string | null;
  setSelectedCustomer: Dispatch<SetStateAction<string | null>>;

  // Document files
  documentFiles: ExtendedFile[];
  setDocumentFiles: Dispatch<SetStateAction<ExtendedFile[]>>;

  // GIS files
  gisFiles: ExtendedFile[];
  setGisFiles: Dispatch<SetStateAction<ExtendedFile[]>>;

  // GIS URLs
  urls: GisUrl[];
  setUrls: Dispatch<SetStateAction<GisUrl[]>>;

  // LLM consent
  allowLLMForDocuments: boolean;
  setAllowLLMForDocuments: Dispatch<SetStateAction<boolean>>;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export const useWizardContext = (): WizardContextValue => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizardContext must be used within a WizardProvider');
  }
  return context;
};

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: FC<WizardProviderProps> = ({ children }) => {
  const [facilityType, setFacilityType] = useState<FacilityType>(
    SiteDueDiligenceFacilityType.DATA_CENTER,
  );
  const [templateMode, setTemplateMode] = useState<TemplateMode | null>('default');
  const [customTemplateFile, setCustomTemplateFile] = useState<ExtendedFile | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [documentFiles, setDocumentFiles] = useState<ExtendedFile[]>([]);
  const [gisFiles, setGisFiles] = useState<ExtendedFile[]>([]);
  const [urls, setUrls] = useState<GisUrl[]>([]);
  const [allowLLMForDocuments, setAllowLLMForDocuments] = useState(true);

  const value: WizardContextValue = {
    facilityType,
    setFacilityType,
    templateMode,
    setTemplateMode,
    customTemplateFile,
    setCustomTemplateFile,
    selectedCustomer,
    setSelectedCustomer,
    documentFiles,
    setDocumentFiles,
    gisFiles,
    setGisFiles,
    urls,
    setUrls,
    allowLLMForDocuments,
    setAllowLLMForDocuments,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};
